// /frontend/src/pages/account/MyOrdersPage.jsx (Updated with Payment button)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import { FaEye, FaSpinner, FaTruck, FaCheck, FaClock, FaMoneyCheck, FaTimesCircle, FaArrowLeft, FaCreditCard } from 'react-icons/fa';
import { orderService } from '../../services/orderService';

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch orders on component mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/account/orders');
      return;
    }
    
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await orderService.getMyOrders();
        console.log('Orders API response:', response); // For debugging
        
        // Handle different possible response structures
        let ordersArray = [];
        if (Array.isArray(response)) {
          // If response is already an array
          ordersArray = response;
        } else if (response.success && response.orders && Array.isArray(response.orders)) {
          // If using success pattern with orders property
          ordersArray = response.orders;
        } else if (response.orders && Array.isArray(response.orders)) {
          // If orders are nested under 'orders' property
          ordersArray = response.orders;
        } else if (response.data && Array.isArray(response.data)) {
          // If orders are nested under 'data' property
          ordersArray = response.data;
        } else {
          // Fallback - try to find any array in the response
          const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
          if (possibleArrays.length > 0) {
            ordersArray = possibleArrays[0];
          }
        }
        
        console.log('Processed orders array:', ordersArray);
        setOrders(ordersArray);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load your orders. Please try again.');
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [isAuthenticated, navigate]);
  
  // Get status icon based on order status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="text-yellow-500" title="Pending" />;
      case 'processing':
        return <FaSpinner className="text-blue-500" title="Processing" />;
      case 'shipped':
        return <FaTruck className="text-purple-500" title="Shipped" />;
      case 'delivered':
        return <FaCheck className="text-green-500" title="Delivered" />;
      case 'canceled':
        return <FaTimesCircle className="text-red-500" title="Canceled" />;
      default:
        return <FaClock className="text-gray-500" title="Unknown" />;
    }
  };
  
  // Get payment status icon
  const getPaymentIcon = (isPaid) => {
    return isPaid 
      ? <FaCheck className="text-green-500" title="Paid" /> 
      : <FaMoneyCheck className="text-orange-500" title="Payment Pending" />;
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-8">My Orders</h1>
          <div className="flex justify-center items-center h-64">
            <FaSpinner className="animate-spin text-teal-800 text-4xl" />
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
          <Link to="/browse" className="flex items-center text-teal-800 hover:text-teal-900">
            <FaArrowLeft className="mr-2" />
            Continue Shopping
          </Link>
        </div>
        
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-md mb-6">
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 text-teal-800 hover:text-teal-900 font-medium"
            >
              Try Again
            </button>
          </div>
        )}
        
        {orders.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <h2 className="text-xl font-medium text-gray-900 mb-2">You haven't placed any orders yet</h2>
            <p className="text-gray-600 mb-6">Browse our collection and find something you'll love!</p>
            <Link 
              to="/browse" 
              className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order._id.substring(order._id.length - 8)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDate(order.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ${order.totalPrice.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.orderItems.length} {order.orderItems.length === 1 ? 'item' : 'items'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(order.status)}
                          <span className="ml-2 text-sm text-gray-700 capitalize">
                            {order.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getPaymentIcon(order.isPaid)}
                          <span className="ml-2 text-sm text-gray-700">
                            {order.isPaid ? 'Paid' : 'Pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                        <Link
                          to={`/account/orders/${order._id}`}
                          className="inline-flex items-center px-3 py-1 border border-teal-800 text-teal-800 rounded hover:bg-teal-800 hover:text-white transition-colors"
                        >
                          <FaEye className="mr-1" />
                          <span>View</span>
                        </Link>
                        
                        {/* Payment button - only shown if payment is pending */}
                        {!order.isPaid && (
                          <Link
                            to={`/payment/${order._id}`}
                            className="inline-flex items-center px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                          >
                            <FaCreditCard className="mr-1" />
                            <span>Pay Now</span>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyOrdersPage;